import apiClient from './client';

export type QuestionType =
  | 'simple'
  | 'complex_with_subs'
  | 'exclusive_subs'
  | 'complex_with_input';

export type QuestionDifficulty = 'easy' | 'medium' | 'hard';

export interface Option {
  id?: string;
  question_id?: string;
  label: string;
  text: string;
  image: string | null;
  is_correct: boolean;
  sort_order: number;
}

export interface SubOption {
  id?: string;
  sub_question_id?: string;
  label: string;
  text: string;
  image: string | null;
  is_correct: boolean;
  sort_order: number;
}

export interface SubQuestion {
  id?: string;
  question_id?: string;
  text: string;
  image: string | null;
  sort_order: number;
  sub_options: SubOption[];
}

export interface Question {
  id: string;
  subject_id: string;
  topic_id: string | null;
  type: QuestionType;
  text: string;
  image: string | null;
  explanation: string | null;
  correct_text_answer: string | null;
  difficulty: QuestionDifficulty | null;
  is_active: boolean;
  version: number;
  options: Option[];
  sub_questions: SubQuestion[];
  created_at: string;
  updated_at: string;
}

export interface QuestionsListResponse {
  data: Question[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface QueryQuestionsParams {
  subject_id?: string;
  topic_id?: string;
  type?: QuestionType;
  page?: number;
  limit?: number;
}

export interface CreateOptionDto {
  label: string;
  text: string;
  image?: string | null;
  is_correct: boolean;
  sort_order?: number;
}

export interface CreateSubOptionDto extends CreateOptionDto {}

export interface CreateSubQuestionDto {
  text: string;
  image?: string | null;
  sort_order?: number;
  sub_options: CreateSubOptionDto[];
}

export interface CreateQuestionDto {
  subject_id: string;
  topic_id?: string | null;
  type?: QuestionType;
  text: string;
  image?: string | null;
  explanation?: string | null;
  correct_text_answer?: string | null;
  difficulty?: QuestionDifficulty | null;
  options: CreateOptionDto[];
  sub_questions?: CreateSubQuestionDto[];
}

export interface UpdateQuestionDto {
  topic_id?: string | null;
  type?: QuestionType;
  text?: string;
  image?: string | null;
  explanation?: string | null;
  correct_text_answer?: string | null;
  difficulty?: QuestionDifficulty | null;
  is_active?: boolean;
  options?: CreateOptionDto[];
  sub_questions?: CreateSubQuestionDto[];
}

export const questionsApi = {
  getAll: async (params: QueryQuestionsParams = {}): Promise<QuestionsListResponse> => {
    const response = await apiClient.get<QuestionsListResponse>('/questions', { params });
    return response.data;
  },

  getById: async (id: string): Promise<Question> => {
    const response = await apiClient.get<Question>(`/questions/${id}`);
    return response.data;
  },

  create: async (dto: CreateQuestionDto): Promise<Question> => {
    const response = await apiClient.post<Question>('/admin/questions', dto);
    return response.data;
  },

  update: async (id: string, dto: UpdateQuestionDto): Promise<Question> => {
    const response = await apiClient.patch<Question>(`/admin/questions/${id}`, dto);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/admin/questions/${id}`);
  },
};
